export type CinematicGrammarComponents = {
  subject: string | null;
  environment: string | null;
  camera: string | null;
  lighting: string | null;
  mood: string | null;
  style: string | null;
  action: string | null;
  colorPalette: string | null;
  composition: string | null;
};

export type CinematicQualityLevel = 'Basic' | 'Good' | 'Professional' | 'Director Level' | 'Hollywood Level';

export type CinematicGrammarReport = {
  components: CinematicGrammarComponents;
  missing: Array<keyof CinematicGrammarComponents>;
  conflicts: string[];
  suggestions: string[];
  vocabulary: {
    cameraTerms: string[];
    lightingTerms: string[];
    cinematographyTerms: string[];
    colorGradingStyles: string[];
  };
  score: number;
  qualityLevel: CinematicQualityLevel;
  enhancedPrompt: string;
};

const first = (text: string, regex: RegExp): string | null => text.match(regex)?.[0]?.trim() || null;

const pick = (lower: string, words: string[], fallback: string): string => words.find((w) => lower.includes(w)) || fallback;

export const CINEMATIC_VOCABULARY = {
  cameraTerms: ['close-up', 'medium shot', 'wide shot', 'low angle', 'overhead', 'dolly', 'tracking shot', 'anamorphic lens', '35mm lens', '50mm lens', '85mm lens'],
  lightingTerms: ['key light', 'rim light', 'fill light', 'low key lighting', 'high key lighting', 'golden hour', 'moonlight', 'volumetric light'],
  cinematographyTerms: ['rule of thirds', 'leading lines', 'subject isolation', 'depth of field', 'cinematic framing', 'foreground layering'],
  colorGradingStyles: ['teal and orange', 'bleach bypass', 'warm cinematic grade', 'neo-noir contrast', 'desaturated drama'],
};

export const parseCinematicGrammar = (prompt: string): CinematicGrammarComponents => {
  const lower = prompt.toLowerCase();

  return {
    subject: first(prompt, /(?:subject|hero|main subject)[:\-]?\s*([^.,\n]+)/i) || prompt.split(/[,.\n]/)[0]?.trim() || null,
    environment: first(prompt, /(desert|forest|city|underwater|mountains|temple|street|space|room|studio)/i),
    camera: first(prompt, /(close-up|medium shot|wide shot|low angle|high angle|eye-level|top-down|overhead|tracking shot|dolly)/i),
    lighting: first(prompt, /(golden hour|moonlight|daylight|low key lighting|high key lighting|volumetric light|rim light|key light)/i),
    mood: first(prompt, /(epic|dramatic|tense|mystical|calm|heroic|dark|moody)/i),
    style: first(prompt, /(cinematic realism|photorealistic|cinematic|neo-noir|filmic|anime|3d render)/i),
    action: first(prompt, /(running|walking|standing|fighting|looking|jumping|charging|holding)/i),
    colorPalette: first(prompt, /(teal and orange|warm tones|cool tones|monochrome|desaturated|vibrant)/i),
    composition: first(prompt, /(rule of thirds|centered composition|leading lines|symmetrical framing|negative space)/i),
  };
};

export const detectCinematicGrammarConflicts = (prompt: string): string[] => {
  const lower = prompt.toLowerCase();
  const conflicts: string[] = [];

  if (/close-up/.test(lower) && /wide shot/.test(lower)) conflicts.push('Camera grammar conflict: close-up with wide shot in same beat.');
  if (/macro/.test(lower) && /aerial/.test(lower)) conflicts.push('Camera grammar conflict: macro with aerial framing.');
  if (/low key lighting/.test(lower) && /high key lighting/.test(lower)) conflicts.push('Lighting grammar conflict: low-key with high-key setup.');
  if (/teal and orange/.test(lower) && /monochrome/.test(lower)) conflicts.push('Color grammar conflict: teal-orange vs monochrome.');

  return conflicts;
};

export const scoreCinematicGrammar = (components: CinematicGrammarComponents, conflicts: string[]): { score: number; qualityLevel: CinematicQualityLevel } => {
  const filled = Object.values(components).filter(Boolean).length;
  const base = Math.min(90, filled * 10);
  const penalty = conflicts.length * 12;
  const score = Math.max(0, Math.min(100, base - penalty));

  if (score >= 90) return { score, qualityLevel: 'Hollywood Level' };
  if (score >= 75) return { score, qualityLevel: 'Director Level' };
  if (score >= 60) return { score, qualityLevel: 'Professional' };
  if (score >= 40) return { score, qualityLevel: 'Good' };
  return { score, qualityLevel: 'Basic' };
};

export const enhanceCinematicGrammarLocal = (prompt: string): string => {
  const c = parseCinematicGrammar(prompt);
  const lower = prompt.toLowerCase();

  const subject = c.subject || 'main cinematic subject';
  const environment = c.environment || pick(lower, ['desert', 'forest', 'city', 'underwater', 'temple'], 'cinematic environment');
  const camera = c.camera || 'low angle cinematic medium shot';
  const lighting = c.lighting || 'golden hour key light with soft rim light';
  const mood = c.mood || 'epic dramatic tone';
  const style = c.style || 'cinematic realism';
  const action = c.action || 'standing with controlled heroic motion';
  const colorPalette = c.colorPalette || 'teal and orange cinematic grade';
  const composition = c.composition || 'rule of thirds with clear subject isolation';

  return [
    `Subject: ${subject}`,
    `Environment: ${environment}`,
    `Camera: ${camera}`,
    `Lighting: ${lighting}`,
    `Mood: ${mood}`,
    `Style: ${style}`,
    `Action: ${action}`,
    `Color palette: ${colorPalette}`,
    `Composition: ${composition}`,
  ].join('. ');
};

export const buildCinematicGrammarReport = (prompt: string): CinematicGrammarReport => {
  const components = parseCinematicGrammar(prompt);
  const missing = (Object.entries(components)
    .filter(([, value]) => !value)
    .map(([key]) => key)) as Array<keyof CinematicGrammarComponents>;
  const conflicts = detectCinematicGrammarConflicts(prompt);

  const suggestions: string[] = [];
  if (!components.lighting) suggestions.push('Add a film lighting setup (key/fill/rim or golden hour).');
  if (!components.camera) suggestions.push('Define camera grammar (shot scale + angle + lens).');
  if (!components.composition) suggestions.push('Add composition intent (rule of thirds, leading lines, centered).');
  if (!components.colorPalette) suggestions.push('Add color grading style for cohesive cinematic tone.');
  if (!components.action) suggestions.push('Add subject action to improve visual storytelling.');

  const { score, qualityLevel } = scoreCinematicGrammar(components, conflicts);
  const enhancedPrompt = enhanceCinematicGrammarLocal(prompt);

  return {
    components,
    missing,
    conflicts,
    suggestions,
    vocabulary: CINEMATIC_VOCABULARY,
    score,
    qualityLevel,
    enhancedPrompt,
  };
};
